/**
 * Per-theme mood for a dungeon interior (q-dungeon-theme-mood-pass): every theme
 * shares the one `dungeon-interior-kit-v1` wall/floor art, so what makes the three
 * read as three places is a multiply tint on that art plus the light it is seen by —
 * no new art, no layout or collision change. A theme with no entry keeps the
 * pre-pass look (`DEFAULT_DUNGEON_MOOD`), so a new theme never renders broken.
 */
import * as THREE from 'three';

export interface DungeonLightMood {
  colour: number;
  intensity: number;
}

export interface DungeonMood {
  ambient: DungeonLightMood;
  /** The per-chamber point light; `boss` replaces it in a boss chamber. */
  torch: DungeonLightMood;
  boss: DungeonLightMood;
  /** Point-light decay exponent — higher pools the light nearer the chamber centre. */
  torchDecay: number;
  /** Multiply tints on the kit's own colours (0xffffff = untouched). */
  floorTint: number;
  wallTint: number;
}

export const DEFAULT_DUNGEON_MOOD: DungeonMood = {
  ambient: { colour: 0x3a4652, intensity: 0.85 },
  torch: { colour: 0xffd9a0, intensity: 0.7 },
  boss: { colour: 0xff8a5c, intensity: 1.1 },
  torchDecay: 1.9,
  floorTint: 0xffffff,
  wallTint: 0xffffff,
};

export const DUNGEON_THEME_MOODS: Readonly<Record<string, DungeonMood>> = {
  // Root-hollows under the Reach: green-black shade, lantern-moss glow, loam floor.
  'verdant-underroot': {
    ambient: { colour: 0x24402c, intensity: 0.7 },
    torch: { colour: 0xc6f09a, intensity: 0.85 },
    boss: { colour: 0xffb36b, intensity: 1.2 },
    torchDecay: 2.1,
    floorTint: 0x5b6a48,
    wallTint: 0x93ab84,
  },
  // Sky-stone ruins: cold pale stone, blue-white light, the floor a shade under the walls.
  'cloud-ruins': {
    ambient: { colour: 0x55657f, intensity: 0.95 },
    torch: { colour: 0xd4e4ff, intensity: 0.8 },
    boss: { colour: 0x9cc2ff, intensity: 1.25 },
    torchDecay: 1.7,
    floorTint: 0x8c93a4,
    wallTint: 0xd2dbec,
  },
  // Ash and ember: soot walls, ember light, a near-black floor (the moss goes brown).
  'ashen-depths': {
    ambient: { colour: 0x3a2622, intensity: 0.6 },
    torch: { colour: 0xff7a3a, intensity: 0.95 },
    boss: { colour: 0xff4426, intensity: 1.35 },
    torchDecay: 2.3,
    floorTint: 0x43382f,
    wallTint: 0x7c6a60,
  },
};

export function moodForTheme(themeId: string): DungeonMood {
  return DUNGEON_THEME_MOODS[themeId] ?? DEFAULT_DUNGEON_MOOD;
}

/** Multiply two 0xRRGGBB colours channel-wise (what a tint does to a flat colour). */
export function multiplyHex(base: number, tint: number): number {
  return new THREE.Color(base).multiply(new THREE.Color(tint)).getHex();
}

/**
 * Tint every mesh under `root` by giving it its OWN material copy — a loaded piece
 * shares its materials with the asset cache, which other interiors and the world
 * draw from. Clones made afterwards share the tinted copies (one per source
 * material), and the run's dispose traverse frees them with the group.
 */
export function tintObject(root: THREE.Object3D, tint: number): void {
  if (tint === 0xffffff) return;
  const multiplier = new THREE.Color(tint);
  const copies = new Map<THREE.Material, THREE.Material>();
  const tinted = (material: THREE.Material): THREE.Material => {
    let copy = copies.get(material);
    if (!copy) {
      copy = material.clone();
      const coloured = copy as THREE.Material & { color?: THREE.Color };
      if (coloured.color instanceof THREE.Color) coloured.color.multiply(multiplier);
      copies.set(material, copy);
    }
    return copy;
  };
  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map(tinted)
      : tinted(mesh.material);
  });
}
